import {
  ApplicationRef,
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
  signal,
} from '@angular/core';
import { DrawerConfig } from './drawer-config';
import { Currency } from 'src/app/model/wallet/currency';
import { OperatorService } from '../operator/operator.service';
import { StorageService } from '../storage/storage.service';
import { GameId } from 'src/app/model/game/game-id';

/** Enum representing all the themes. Define these themes in the theme/ folder.
 * Use the class name of the theme as the value of the Theme enum
 */
export enum Theme {
  Clinch = 'clinch',
  BetOpenly = 'betopenly',
  Maincard = 'maincard',
  Crypto = 'crypto',
  Glow = 'glow',
  THNDR = 'thndr',
  LotteryNow = 'lotterynow',
  GameChampions = 'gamechampions',
  GamerSaloon = 'gamersaloon',
  Kutt = 'kutt',
}

/**
 * Class representing a ThemeService
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private renderer: Renderer2;
  private appRef = inject(ApplicationRef);
  private storageService = inject(StorageService);

  private currentThemeSignal = signal<Theme>(Theme.Clinch);
  public readonly currentTheme = this.currentThemeSignal.asReadonly();

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);

    if (OperatorService.IsDemo) {
      this.storageService
        .getItem<Theme>('demo-theme')
        .then((theme) => {
          if (theme) {
            this.switch(theme);
          } else {
            this.switch(Theme.Crypto);
          }
        })
        .catch(() => {
          this.switch(Theme.Crypto);
        });
    } else {
      this.switch(this.currentTheme());
    }
  }

  switch(theme: Theme): void {
    if (OperatorService.IsDemo) {
      this.storageService.setItem('demo-theme', theme);
    }
    this.applyTheme(theme);
  }

  icon(name: string): string {
    return `assets/icons/${this.iconSet()}/${name}.svg`;
  }

  iconSet(): string {
    return 'atlas';
  }

  blocksMultiplierPath(): string {
    return `assets/games/blocks/${this.currentTheme()}/block_multiplier.png`;
  }

  blackjackAssetPath(): string {
    return `assets/games/blackjack/${this.currentTheme()}`;
  }

  blocksAssetPath(): string {
    return `assets/games/blocks/${this.currentTheme()}`;
  }

  solitaireAssetPath(): string {
    return `assets/games/solitaire/${this.currentTheme()}`;
  }

  twentyOneAssetPath(): string {
    return `assets/games/twentyone/${this.currentTheme()}`;
  }

  imageUrl(name: string): string {
    return `assets/images/${this.currentTheme()}/${name}`;
  }

  wordmarkUrl(gameId: GameId): string {
    return `assets/images/${this.currentTheme()}/${gameId}-wordmark.png`;
  }

  appicon(): string {
    return `assets/images/${this.currentTheme()}/clinch.png`;
  }

  imagesFolder(): string {
    return this.currentTheme() as string;
  }

  confettiColors(): string[] {
    return config.get(this.currentTheme())?.confettiColors || ['#9EFF00'];
  }

  buttonConfig() {
    const defaultConfig = {
      primary: {
        outline: false,
        shadow: false,
        gradient: false,
      },
      iconPosition: 'right',
      textTransform: 'none',
    };

    return config.get(this.currentTheme())?.button || defaultConfig;
  }

  drawerConfig(): DrawerConfig {
    return {
      hasGlow: config.get(this.currentTheme())?.payDrawer.hasGlow ? true : false,
      hasGameIcon: config.get(this.currentTheme())?.payDrawer.hasGameIcon ? true : false,
      closeButtonPosition:
        config.get(this.currentTheme())?.payDrawer.closeButtonPosition === 'left'
          ? 'absolute-top-left-drawer'
          : 'absolute-top-right-drawer',
      closeButtonSize:
        config.get(this.currentTheme())?.payDrawer.closeButtonSize === 'small' ? 'small' : 'medium',
      closeButtonInverted: config.get(this.currentTheme())?.payDrawer.closeButtonInverted
        ? true
        : false,
      gradient: config.get(this.currentTheme())?.payDrawer.gradient,
    };
  }

  gameTitleConfig() {
    return (
      config.get(this.currentTheme())?.gameTitle || { hasOutlineGlow: false, capitalize: false }
    );
  }

  gameFontFamily() {
    return config.get(this.currentTheme())?.gameFontFamily || 'DM Sans';
  }

  tabConfig() {
    return config.get(this.currentTheme())?.tab || { style: 'line' };
  }

  spinnerConfig() {
    return config.get(this.currentTheme())?.spinner || { isCustom: false };
  }

  leaderboardConfig() {
    return config.get(this.currentTheme())?.leaderboard || { iconLeft: false };
  }

  iconFolder(): string {
    return config.get(this.currentTheme())?.iconFolder || 'atlas';
  }

  currencyIcon(): string | undefined {
    return config.get(this.currentTheme())?.currencyIcon;
  }

  hasCustomMedal(): boolean {
    return config.get(this.currentTheme())?.customMedal ? true : false;
  }

  isClosable(): boolean {
    return config.get(this.currentTheme())?.closable ? true : false;
  }

  isPracticeFirst(): boolean {
    return config.get(this.currentTheme())?.wagerDrawer?.practiceFirst ? true : false;
  }

  hasBackgroundImg(): boolean {
    return config.get(this.currentTheme())?.hasBackgroundImg ? true : false;
  }

  hasActivityFeed(): boolean {
    return config.get(this.currentTheme())?.hasActivityFeed ? true : false;
  }

  hasProfileIconButton(): boolean {
    return config.get(this.currentTheme())?.hasProfileIconButton ? true : false;
  }

  headerTextTransform(): string {
    return config.get(this.currentTheme())?.headerTextTransform || 'none';
  }

  headerHasGameIcon(): boolean {
    return config.get(this.currentTheme())?.header?.hasGameIcon ? true : false;
  }

  headerHasWordmark(): boolean {
    return config.get(this.currentTheme())?.header?.hasWordmark ? true : false;
  }

  resultCardFlat(): boolean {
    return config.get(this.currentTheme())?.resultCard?.flat ? true : false;
  }

  currency(): Currency {
    return config.get(this.currentTheme())?.currency || Currency.USD;
  }

  formatNumber(value: number): string {
    const useFormatting = config.get(this.currentTheme())?.numberFormatting ?? false;
    return useFormatting ? value.toLocaleString() : `${value}`;
  }

  showBalance(): boolean {
    return config.get(this.currentTheme())?.showBalance !== false;
  }

  private applyTheme(theme: Theme): void {
    const body = document.body;
    this.removeThemes();
    this.renderer.addClass(body, theme);
    this.currentThemeSignal.set(theme);
    this.appRef.tick();
  }

  private removeThemes(): void {
    const body = document.body;
    const themes = Object.values(Theme);
    themes.forEach((theme) => {
      this.renderer.removeClass(body, theme);
    });
  }
}

const config = new Map([
  [
    Theme.Kutt,
    {
      showBalance: true,
      currency: Currency.USD,
      button: {
        textTransform: 'none',
        primary: {
          outline: false,
          shadow: false,
        },
        iconPosition: 'right',
      },
      iconFolder: 'atlas',
      closable: true,
      hasActivityFeed: true,
      hasProfileIconButton: true,
      hasBackgroundImg: true,
      resultCard: {
        flat: true,
      },
      payDrawer: {
        hasGameIcon: true,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: false,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: false,
        capitalize: false,
      },
      gameFontFamily: 'DM Sans',
      tab: {
        style: 'line',
      },
      numberFormatting: true,
      confettiColors: ['#9EFF00'],
      header: {
        hasGameIcon: true,
        hasWordmark: false,
      },
    },
  ],
  [
    Theme.Clinch,
    {
      showBalance: true,
      currency: Currency.USD,
      button: {
        textTransform: 'none',
        primary: {
          outline: false,
          shadow: false,
        },
        iconPosition: 'right',
      },
      iconFolder: 'atlas',
      hasActivityFeed: true,
      hasProfileIconButton: true,
      payDrawer: {
        hasGameIcon: true,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: false,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: false,
        capitalize: false,
      },
      gameFontFamily: 'DM Sans',
      tab: {
        style: 'line',
      },
      numberFormatting: true,
      confettiColors: ['#9EFF00'],
      header: {
        hasGameIcon: false,
        hasWordmark: false,
      },
    },
  ],
  [
    Theme.GameChampions,
    {
      showBalance: true,
      currency: Currency.USD,
      button: {
        textTransform: 'none',
        primary: {
          outline: false,
          shadow: false,
        },
        iconPosition: 'right',
      },
      iconFolder: 'atlas',
      closable: false,
      hasBackgroundImg: true,
      hasActivityFeed: true,
      hasProfileIconButton: true,
      payDrawer: {
        hasGameIcon: true,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: false,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: false,
        capitalize: false,
      },
      gameFontFamily: 'Inter',
      tab: {
        style: 'line',
      },
      numberFormatting: true,
      confettiColors: ['#9EFF00'],
      header: {
        hasGameIcon: true,
        hasWordmark: false,
      },
    },
  ],
  [
    Theme.GamerSaloon,
    {
      showBalance: true,
      currency: Currency.USD,
      button: {
        textTransform: 'none',
        primary: {
          outline: false,
          shadow: false,
        },
        iconPosition: 'right',
      },
      iconFolder: 'atlas',
      closable: true,
      hasBackgroundImg: false,
      hasActivityFeed: true,
      hasProfileIconButton: true,
      payDrawer: {
        hasGameIcon: true,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: false,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: false,
        capitalize: false,
      },
      gameFontFamily: 'Inter',
      tab: {
        style: 'line',
      },
      numberFormatting: true,
      confettiColors: ['#4CD964'],
      header: {
        hasGameIcon: false,
        hasWordmark: false,
      },
    },
  ],
  [
    Theme.LotteryNow,
    {
      showBalance: false,
      currency: Currency.USD,
      button: {
        textTransform: 'none',
        primary: {
          outline: false,
          shadow: false,
        },
        iconPosition: 'right',
      },
      iconFolder: 'font-awesome/thin',
      customMedal: true,
      hasBackgroundImg: true,
      hasActivityFeed: false,
      hasProfileIconButton: false,
      payDrawer: {
        hasGameIcon: true,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: true,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: true,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: false,
        capitalize: false,
      },
      gameFontFamily: 'DM Sans',
      tab: {
        style: 'line',
      },
      numberFormatting: true,
      confettiColors: ['#9EFF00'],
      header: {
        hasGameIcon: false,
        hasWordmark: true,
      },
    },
  ],
  [
    Theme.BetOpenly,
    {
      showBalance: true,
      currency: Currency.USD,
      button: {
        textTransform: 'capitalize',
        primary: {
          outline: false,
          shadow: false,
          gradient: true,
        },
        iconPosition: 'right',
      },
      iconFolder: 'atlas',
      closable: true,
      headerTextTransform: 'capitalize',
      hasActivityFeed: true,
      hasProfileIconButton: true,
      spinner: {
        isCustom: true,
      },
      payDrawer: {
        hasGameIcon: true,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: false,
        gradient: 'linear',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: false,
        capitalize: false,
      },
      gameFontFamily: 'Helvetica Neue',
      tab: {
        style: 'line',
      },
      numberFormatting: true,
      confettiColors: ['#0BBD07'],
      header: {
        hasGameIcon: true,
        hasWordmark: false,
      },
    },
  ],
  [
    Theme.Maincard,
    {
      showBalance: true,
      currency: Currency.MCN,
      button: {
        textTransform: 'none',
        primary: {
          outline: true,
          shadow: true,
        },
        iconPosition: 'right',
      },
      iconFolder: 'atlas',
      currencyIcon: 'currency-mcn',
      customMedal: true,
      hasBackgroundImg: true,
      hasActivityFeed: true,
      hasProfileIconButton: true,
      payDrawer: {
        hasGameIcon: false,
        hasGlow: true,
        closeButtonPosition: 'right',
        closeButtonSize: 'small',
        closeButtonInverted: false,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: true,
      },
      gameTitle: {
        hasOutlineGlow: true,
        capitalize: true,
      },
      gameFontFamily: 'Raleway',
      tab: {
        style: 'circle',
      },
      numberFormatting: false,
      confettiColors: ['#4CAA00'],
      header: {
        hasGameIcon: false,
        hasWordmark: false,
      },
    },
  ],
  [
    Theme.Crypto,
    {
      showBalance: true,
      currency: Currency.USD,
      button: {
        textTransform: 'none',
        primary: {
          outline: false,
          shadow: false,
        },
        iconPosition: 'right',
      },
      closable: true,
      iconFolder: 'atlas',
      hasActivityFeed: true,
      payDrawer: {
        hasGameIcon: true,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: false,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: false,
        capitalize: false,
      },
      gameFontFamily: 'DM Sans',
      tab: {
        style: 'line',
      },
      numberFormatting: true,
      confettiColors: ['#9EFF00'],
      header: {
        hasGameIcon: true,
        hasWordmark: false,
      },
    },
  ],
  [
    Theme.Glow,
    {
      showBalance: true,
      currency: Currency.USD,
      button: {
        textTransform: 'none',
        primary: {
          outline: true,
          shadow: true,
        },
        iconPosition: 'right',
      },
      closable: true,
      iconFolder: 'atlas',
      hasBackgroundImg: true,
      hasActivityFeed: true,
      hasProfileIconButton: true,
      payDrawer: {
        hasGameIcon: false,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: false,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: true,
        capitalize: true,
      },
      gameFontFamily: 'Raleway',
      tab: {
        style: 'circle',
      },
      numberFormatting: false,
      confettiColors: ['#4CAA00'],
      header: {
        hasGameIcon: false,
        hasWordmark: false,
      },
    },
  ],
  [
    Theme.THNDR,
    {
      showBalance: false,
      currency: Currency.USD,
      button: {
        textTransform: 'none',
        primary: {
          outline: false,
          shadow: false,
        },
        iconPosition: 'right',
      },
      closable: true,
      iconFolder: 'atlas',
      hasActivityFeed: true,
      hasProfileIconButton: true,
      payDrawer: {
        hasGameIcon: true,
        hasGlow: true,
        closeButtonPosition: 'left',
        closeButtonSize: 'medium',
        closeButtonInverted: false,
        gradient: 'circle',
      },
      wagerDrawer: {
        practiceFirst: false,
      },
      leaderboard: {
        iconLeft: false,
      },
      gameTitle: {
        hasOutlineGlow: false,
        capitalize: false,
      },
      gameFontFamily: 'DM Sans',
      tab: {
        style: 'line',
      },
      numberFormatting: true,
      confettiColors: ['#9EFF00'],
      header: {
        hasGameIcon: false,
        hasWordmark: false,
      },
    },
  ],
]);
